/**
 * AdminInbox — In-app admin dashboard for Sean's Team.
 *
 * Why this exists:
 *  - All third-party email services (Resend, SMTP, etc.) have been REMOVED from
 *    the booking flow per user requirement. Bookings are now reviewed entirely
 *    inside this dashboard. Zero external dependencies = $0 forever.
 *
 * Access:
 *  - Reached via the URL hash #admin (so it doesn't show up in nav for kids).
 *  - Password gate on first visit. Password stored in sessionStorage so it
 *    doesn't survive a browser restart.
 *  - Auto-polls every 30 seconds for new bookings.
 *  - Browser-tab notification badge ("(2) Sean Lab") when new bookings arrive
 *    while the tab is open in the background — no notification permission needed.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const ADMIN_KEY_STORAGE = "seanlab_admin_key";

const TIER_LABELS = {
  digital:  "Digital (Virtual)",
  regional: "Regional In-Person",
  national: "National / Flagship",
};

const STATUS_STYLES = {
  new:       { bg: "var(--red)",    fg: "#fff",         label: "NEW" },
  reviewing: { bg: "var(--sun)",    fg: "var(--ink)",   label: "REVIEWING" },
  accepted:  { bg: "var(--green)",  fg: "#fff",         label: "ACCEPTED" },
  declined:  { bg: "#9aa1b5",       fg: "#fff",         label: "DECLINED" },
};

export default function AdminInbox({ onExit }) {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, new_count: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [filter, setFilter] = useState("all");
  const lastSeenCountRef = useRef(0);
  const originalTitleRef = useRef(document.title);

  // ------- Auth -------
  const tryLogin = useCallback(async (key) => {
    setErr("");
    try {
      await api.post("/admin/login", null, { headers: { "X-Admin-Key": key } });
      sessionStorage.setItem(ADMIN_KEY_STORAGE, key);
      setAdminKey(key);
      setAuthed(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed.");
      sessionStorage.removeItem(ADMIN_KEY_STORAGE);
      setAuthed(false);
    }
  }, []);

  // Auto-login if a key is in sessionStorage
  useEffect(() => { if (adminKey && !authed) tryLogin(adminKey); }, [adminKey, authed, tryLogin]);

  // ------- Fetch + poll -------
  const fetchBookings = useCallback(async () => {
    if (!authed || !adminKey) return;
    setLoading(true);
    try {
      const res = await api.get("/admin/bookings", { headers: { "X-Admin-Key": adminKey } });
      setData(res.data);
      // Tab-title ping when new bookings arrive while tab is in background
      const newCount = res.data.new_count || 0;
      if (newCount > lastSeenCountRef.current && document.hidden) {
        document.title = `(${newCount}) 📜 New booking — Sean Lab`;
      } else if (newCount === 0) {
        document.title = originalTitleRef.current;
      }
      lastSeenCountRef.current = newCount;
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(ADMIN_KEY_STORAGE);
        setAuthed(false);
      }
    } finally { setLoading(false); }
  }, [authed, adminKey]);

  useEffect(() => {
    if (!authed) return;
    fetchBookings();
    const id = setInterval(fetchBookings, 30000); // poll every 30s
    const onVisible = () => { if (!document.hidden) document.title = originalTitleRef.current; };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); document.title = originalTitleRef.current; };
  }, [authed, fetchBookings]);

  const updateStatus = async (id, status) => {
    try {
      await api.post(`/admin/bookings/${id}/status`, { status }, { headers: { "X-Admin-Key": adminKey } });
      fetchBookings();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not update status."); }
  };

  const logout = () => {
    sessionStorage.removeItem(ADMIN_KEY_STORAGE);
    setAdminKey(""); setAuthed(false); setPwInput("");
  };

  // ============= LOGIN GATE =============
  if (!authed) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center px-4" style={{ background: "var(--ink)" }} data-testid="admin-login">
        <div className="card-flat bg-white max-w-md w-full">
          <div className="eyebrow text-[var(--red)] mb-2">// admin · sean's team only</div>
          <h2 className="font-display text-3xl text-[var(--ink)] mb-2">Booking Inbox</h2>
          <p className="text-sm text-[var(--ink-soft)] mb-4">Enter the admin password to review all incoming bookings.</p>
          <form onSubmit={(e) => { e.preventDefault(); tryLogin(pwInput); }}>
            <input
              type="password"
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              className="field"
              placeholder="••••••••••••"
              data-testid="admin-password-input"
              autoFocus
            />
            {err && <div className="mt-3 text-sm font-bold text-[var(--red)]" data-testid="admin-login-error">{err}</div>}
            <div className="mt-4 flex gap-2 flex-wrap">
              <button type="submit" className="btn btn-red" data-testid="admin-login-submit">🔓 Unlock Inbox</button>
              <button type="button" onClick={onExit} className="btn-ghost" data-testid="admin-exit">← Back to site</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ============= DASHBOARD =============
  const visibleItems = filter === "all" ? data.items : data.items.filter((b) => b.status === filter);
  const counts = data.items.reduce((acc, b) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen pb-12" style={{ background: "var(--paper)" }} data-testid="admin-inbox">
      {/* Header */}
      <div className="sticky top-0 z-10 px-4 py-3 flex flex-wrap items-center gap-2" style={{ background: "var(--ink)", color: "#fff", borderBottom: "3px solid var(--ink)" }}>
        <div className="font-display text-xl mr-2">📜 Booking Inbox</div>
        <span className="pill pill-red text-xs" data-testid="admin-new-count">🔴 {data.new_count} new</span>
        <span className="pill" style={{ background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: "0.7rem" }} data-testid="admin-total">{data.total} total</span>
        {loading && <span className="text-xs opacity-70 animate-pulse">refreshing…</span>}
        <div className="ml-auto flex gap-2 flex-wrap">
          <button onClick={fetchBookings} className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: "var(--sun)", color: "var(--ink)" }} data-testid="admin-refresh">🔄 Refresh</button>
          <button onClick={onExit} className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }} data-testid="admin-exit-btn">← Site</button>
          <button onClick={logout} className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: "var(--red)", color: "#fff" }} data-testid="admin-logout">🔒 Logout</button>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-5xl mx-auto px-4 mt-4 flex flex-wrap gap-2">
        {["all", "new", "reviewing", "accepted", "declined"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className="px-3 py-1.5 rounded-full text-xs font-bold capitalize"
            style={{
              background: filter === s ? "var(--ink)" : "#fff",
              color: filter === s ? "#fff" : "var(--ink)",
              border: "2.5px solid var(--ink)",
              boxShadow: filter === s ? "2px 2px 0 0 var(--ink)" : "none",
            }}
            data-testid={`admin-filter-${s}`}
          >
            {s} {s !== "all" && counts[s] ? `(${counts[s]})` : ""}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="max-w-5xl mx-auto px-4 mt-4 space-y-3" data-testid="admin-booking-list">
        {visibleItems.length === 0 && (
          <div className="card-flat bg-white text-center py-10 text-[var(--ink-soft)]" data-testid="admin-empty">
            <div className="text-4xl mb-2">📭</div>
            <div className="font-fun">{filter === "all" ? "No bookings yet." : `No ${filter} bookings.`}</div>
          </div>
        )}
        {visibleItems.map((b) => {
          const style = STATUS_STYLES[b.status] || STATUS_STYLES.new;
          const created = new Date(b.created_at).toLocaleString();
          return (
            <article key={b.id} className="card-flat bg-white" data-testid={`admin-booking-${b.id.slice(0,8)}`}>
              <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-display text-lg text-[var(--ink)] leading-tight">{b.org_name}</div>
                  <div className="text-xs text-[var(--ink-soft)] font-mono">ref {b.id.slice(0, 8)} · {created}</div>
                </div>
                <span className="pill text-[0.65rem]" style={{ background: style.bg, color: style.fg, whiteSpace: "nowrap" }}>{style.label}</span>
              </div>

              <div className="grid sm:grid-cols-2 gap-2 text-sm mb-3">
                <div><span className="label-tag">contact</span><br/>{b.contact_name}</div>
                <div><span className="label-tag">tier</span><br/>{TIER_LABELS[b.tier] || b.tier}</div>
                <div className="min-w-0"><span className="label-tag">email</span><br/><a href={`mailto:${b.contact_email}`} className="text-[var(--red)] font-bold break-all">{b.contact_email}</a></div>
                <div><span className="label-tag">phone</span><br/><a href={`tel:${b.contact_phone}`} className="text-[var(--red)] font-bold">{b.contact_phone}</a></div>
                {b.event_date && <div><span className="label-tag">date</span><br/>{b.event_date}</div>}
                {(b.event_city || b.event_state) && <div><span className="label-tag">location</span><br/>{[b.event_city, b.event_state].filter(Boolean).join(", ")}</div>}
                {b.audience_size > 0 && <div><span className="label-tag">audience</span><br/>{b.audience_size.toLocaleString()}</div>}
              </div>

              {b.description && (
                <div className="card-flat tint-cream mb-3" style={{ padding: "0.6rem 0.8rem" }}>
                  <div className="label-tag mb-1">description</div>
                  <div className="text-sm text-[var(--ink)] whitespace-pre-wrap break-words">{b.description}</div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2" style={{ borderTop: "2px dashed #d8c890" }}>
                <div className="label-tag self-center mr-1">change status:</div>
                {["new", "reviewing", "accepted", "declined"].map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(b.id, s)}
                    disabled={b.status === s}
                    className="px-2.5 py-1 rounded-full text-[0.7rem] font-bold capitalize"
                    style={{
                      background: b.status === s ? STATUS_STYLES[s].bg : "#fff",
                      color: b.status === s ? STATUS_STYLES[s].fg : "var(--ink)",
                      border: "2px solid var(--ink)",
                      opacity: b.status === s ? 1 : 0.7,
                      cursor: b.status === s ? "default" : "pointer",
                    }}
                    data-testid={`admin-set-${s}-${b.id.slice(0,8)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
